import { useState } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Check,
  Combobox,
  IconTitle,
  InformationBlock,
  PageTitle,
  Text
} from '@dec/shared';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { CardChecklist, InputCursorText } from 'react-bootstrap-icons';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { notification } from '@/app/core/notifications/notification';

import { useCatalogList } from '../../hooks/useCatalogList';
import { type ColDefinitionModel } from '../../models/Catalog';
import { type CatalogTypeModel } from '../../models/Catalog';
import { type CatalogValue } from '../../models/Catalog';
import { catalogService } from '../../services/catalog.service';

import { CatalogDefinitionModal } from './CatalogDefinitionModal';

interface CatalogEditForm {
  id: string;
  description: string;
  parent: CatalogValue;
  isPrivate: boolean;
}

interface CatalogEditProps {
  original?: CatalogTypeModel;
  handleCancel: () => void;
  handleFinish: () => void;
}

export const CatalogEdit = ({ handleCancel, handleFinish, original }: CatalogEditProps) => {
  const { data: parents } = useCatalogList();
  const [showModal, setShowModal] = useState<boolean>(false);

  // se configuran las validaciones del formulario, cuales son las reglas que debe cumplir
  const schema: yup.ObjectSchema<CatalogEditForm> = yup.object({
    id: yup.string().nullable().required(),
    description: yup.string().nullable().required(),
    parent: yup.mixed<CatalogValue>().nullable().required(),
    isPrivate: yup.boolean().nullable().required()
  });

  const initialValues: CatalogTypeModel | undefined = original
    ? {
        id: original.id,
        description: original.description,
        isPrivate: original.isPrivate,
        parent: {
          id: original.parentId
        }
      }
    : undefined;

  // se construye el formulario con los valores recibiods y las reglas por aplicar
  // esto nos devuelve los métodos para su gestión (submit, reset, etc)
  const methods = useForm<CatalogEditForm>({
    defaultValues: initialValues,
    resolver: yupResolver(schema)
  });
  // vamos a utilizar el manejo de submit y el manejo de reset
  const { handleSubmit } = methods;

  const handleSave = async (values: CatalogEditForm) => {
    // se va a guardar los datos del formulario
    const content: CatalogTypeModel = {
      id: values.id,
      description: values.description!,
      parentId: values.parent?.id,
      isPrivate: values.isPrivate,
      // se agregan las definiciones particulares
      definitions: definitions
    };

    if (original) {
      // si se trata de una edición
      await catalogService.updateCatalog(content, original.id!);
    } else {
      await catalogService.createCatalog(content);
    }

    notification.success(t('common.messages.savedSucessfully'));

    handleFinish();
  };

  // definiciones adicionales para el catalogo
  const [definitions, setDefinitions] = useState<Array<ColDefinitionModel>>(
    original?.definitions ?? []
  );

  const handleRemoveDefinition = (def: ColDefinitionModel) => {
    setDefinitions((definitions) => definitions.filter((d) => d.field != def.field));
  };

  const handleDefinition = (definition: ColDefinitionModel) => {
    // agregamos la definicion a nuestro arreglo
    setDefinitions(definitions.concat([definition]));

    console.log(definition);
    setShowModal(false);

    return true;
  };

  const handleDefinitionCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      <PageTitle title={t('catalog.catalog')} icon={CardChecklist} />

      <CatalogDefinitionModal
        show={showModal}
        handleCancel={handleDefinitionCancel}
        handleFinish={handleDefinition}
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSave)} autoComplete="off">
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="card">
                <div className="card-body">
                  <IconTitle
                    title={t('catalog.definition')}
                    icon={InputCursorText}
                    subtitle={t('catalog.definitionDescription')}
                  />

                  <div className="row">
                    <div className="col-12">
                      <Text
                        label={t('catalog.fields.id')}
                        name="id"
                        readonly={original != undefined}
                      />
                    </div>
                    <div className="col-12">
                      <Combobox
                        code="id"
                        display="id"
                        data={parents}
                        label={t('catalog.fields.parent')}
                        name="parent"
                      />
                    </div>
                    <div className="col-12">
                      <Text label={t('catalog.fields.description')} name="description" />
                      {/* <TranslatesWrapper
                key={translatedProperties.description}
                availableLanguages={availableLanguages}
                defaultLang={Env.defaultLang}
                onCreate={(language: Language) => {
                  const mapped = mapTranslations();
                  const item = mapped?.find((c) => c.key == translatedProperties.description);
                  if (item) {
                    item.languages = item.languages || [];
                    item.languages.push(language);
                    setTranslations([...mapped]);
                  } else {
                    setTranslations([
                      ...(mapped || []),
                      { key: translatedProperties.description, languages: [language] }
                    ]);
                  }
                }}
                onEdit={(language: Language) => {
                  const mapped = mapTranslations();
                  const item = mapped?.find((c) => c.key == translatedProperties.description);
                  if (item) {
                    const lang = item.languages.find((l: Language) => l.lang === language.lang);
                    if (lang) lang.value = language.value;
                    setTranslations([...mapped]);
                  }
                }}
                languages={
                  translations?.find((c) => c.key == translatedProperties.description)?.languages
                }
              /> */}
                    </div>

                    <div className="col-12">
                      <Check label={t('catalog.fields.private')} name="isPrivate" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-8">
              {/* se muestran todas las definiciones asociadas con el item de catalogo */}
              <div>
                <IconTitle
                  title={t('catalog.fieldAdditional')}
                  icon={InputCursorText}
                  subtitle={t('catalog.fieldAdditionalDescription')}
                />

                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="secondary"
                        action={ButtonAction.add}
                        text={t('catalog.fieldAdditionalAdd')}
                        className="btn-sm"
                        onClick={() => {
                          setShowModal(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  {definitions.length == 0 && (
                    <div className="col-12">
                      <div className="alert alert-info">{t('catalog.fieldAdditionalZero')} </div>
                    </div>
                  )}
                  {definitions.map((item: ColDefinitionModel) => (
                    <div className={`col-6 col-md-12 col-lg-6 col-xxl-4`} key={item.field}>
                      <div className="card">
                        <div className="card-header bg-soft">{item.field}</div>
                        <div className="card-body">
                          <InformationBlock
                            title={item.type}
                            type="text"
                            value={item.label}
                            icon={InputCursorText}
                          ></InformationBlock>

                          <div className="d-flex justify-content-end">
                            <Button
                              variant="danger"
                              outline={true}
                              action={ButtonAction.delete}
                              className="btn-sm ms-3"
                              onClick={() => {
                                handleRemoveDefinition(item);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <BtnToolbar>
            <BtnGroup>
              <Button action={ButtonAction.cancel} variant="secondary" onClick={handleCancel} />
            </BtnGroup>
            <BtnGroup>
              <Button action={ButtonAction.save} variant="primary" type={'submit'} />
            </BtnGroup>
          </BtnToolbar>
        </form>
      </FormProvider>
    </>
  );
};
